import React, { Component } from 'react';
import { educationRef } from '../config/firebase';
import { onValue, set, child, update, remove } from 'firebase/database';
import PageLoader from './PageLoader';
import Cropper from 'react-easy-crop';
import { getCroppedImg, dataUrlToBlob, extensionForMime } from '../actions/cropUtils';
import { supabase, EDUCATION_BUCKET, storagePathFromUrl } from '../config/supabase';
import { showToast } from '../actions/toastBus';
import {
    LuPlus,
    LuSave,
    LuTrash2,
    LuX,
    LuSparkles,
    LuPencilLine,
    LuCalendar,
    LuPercent,
    LuImagePlus
} from 'react-icons/lu';
import '../stylesheet/style.css';
import '../stylesheet/Education.css';

export default class Education extends Component {

    constructor(props) {
        super(props);
        this.state = {
            education: [],
            contentLoading: true,
            isLoggedIn: false,
            draftEducation: { name: '', desc: '', passout: '', percentage: '', img: '' },
            // null = the popup is adding a new entry; otherwise the id
            // (real Firebase index) of the entry being edited.
            editingId: null,
            modalOpen: false,
            modalClosing: false,
            cropModalOpen: false,
            uploadedImage: null,
            uploadedImageType: null,
            croppedAreaPixels: null,
            crop: { x: 0, y: 0 },
            zoom: 1
        };
    };

    componentDidMount() {
        this.setState({ isLoggedIn: localStorage.getItem('adminLoggedIn') });
        onValue(educationRef, (snapshot) => {
            const data = snapshot.val();
            const education = Array.isArray(data)
                ? data
                : (data ? Object.values(data) : []);
            this.setState({
                education,
                contentLoading: false
            });
        });
        document.addEventListener('keydown', this.handleKeyDown);
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyDown);
        window.clearTimeout(this._modalTimer);
    }

    handleKeyDown = (event) => {
        if (event.key !== 'Escape') return;
        if (this.state.cropModalOpen) {
            this.setState({ cropModalOpen: false });
        } else if (this.state.modalOpen) {
            this.closeModal();
        }
    }

    // Same popup handles add and edit: null editingId = blank form for a
    // new entry, otherwise the form is pre-filled from that card and
    // saves back to its own Firebase key instead of creating a new one.
    openAddModal = () => {
        this.setState({
            editingId: null,
            draftEducation: { name: '', desc: '', passout: '', percentage: '', img: '' },
            modalOpen: true,
            modalClosing: false
        });
    }

    openEditModal = (id, education) => {
        this.setState({
            editingId: id,
            draftEducation: {
                name: education.name || '',
                desc: education.desc || '',
                passout: education.passout || '',
                percentage: education.percentage || '',
                img: education.img || ''
            },
            modalOpen: true,
            modalClosing: false
        });
    }

    closeModal = () => {
        // Match the exit-animation timing (see .education-modal-* in
        // Education.css) before actually unmounting.
        this.setState({ modalClosing: true });
        window.clearTimeout(this._modalTimer);
        this._modalTimer = window.setTimeout(() => {
            this.setState({ modalOpen: false, modalClosing: false, editingId: null });
        }, 320);
    }

    handleDraftChange = (field, value) => {
        this.setState((prev) => ({
            draftEducation: { ...prev.draftEducation, [field]: value }
        }));
    }

    openCropModal = () => {
        this.fileInput.click();
    };

    handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                this.setState({
                    uploadedImage: reader.result,
                    // Kept so the crop/upload below can output the same
                    // format instead of forcing everything through JPEG.
                    uploadedImageType: file.type || 'image/jpeg',
                    cropModalOpen: true,
                    croppedAreaPixels: null,
                    crop: { x: 0, y: 0 },
                    zoom: 1
                });
            };
            reader.readAsDataURL(file);
        }

        // Allow re-uploading the same file
        event.target.value = '';
    };

    onCropComplete = (_, croppedAreaPixels) => {
        this.setState({ croppedAreaPixels });
    };

    handleCropSave = async () => {
        const mimeType = this.state.uploadedImageType || 'image/jpeg';
        const croppedImage = await getCroppedImg(
            this.state.uploadedImage,
            this.state.croppedAreaPixels,
            mimeType
        );

        // Instant local preview while the upload happens in the
        // background - like Company logo/Profile photo elsewhere, the
        // field only actually gets written to Firebase when Save/Add
        // is clicked.
        this.handleDraftChange('img', croppedImage);
        this.setState({
            cropModalOpen: false,
            uploadedImage: null,
            croppedAreaPixels: null
        });

        try {
            const blob = dataUrlToBlob(croppedImage);
            const fileName = `education-${Date.now()}.${extensionForMime(blob.type)}`;

            const { error: uploadError } = await supabase.storage
                .from(EDUCATION_BUCKET)
                .upload(fileName, blob, { contentType: blob.type, upsert: true });
            if (uploadError) throw uploadError;

            const { data } = supabase.storage.from(EDUCATION_BUCKET).getPublicUrl(fileName);
            this.handleDraftChange('img', data.publicUrl);
        } catch (error) {
            console.error('Error uploading education logo:', error);
            showToast(`Could not upload the logo: ${error.message || 'please try again.'}`, 'error');
        }
    };

    // Logo is optional - lets the admin clear one back out instead of
    // being stuck once uploaded.
    removeImage = () => {
        this.handleDraftChange('img', '');
    };

    saveEducation = () => {
        const { name, desc, passout, percentage, img } = this.state.draftEducation;
        if (!name || !desc) return showToast('Fill in every field before saving.', 'error');
        update(educationRef, {
            [this.state.editingId]: { name, desc, passout: passout || '', percentage: percentage || '', img: img || '' }
        })
            .then(() => {
                this.closeModal();
                showToast('Education updated', 'success');
            })
            .catch((error) => {
                console.error('Error updating education:', error);
                showToast('Could not update this education entry - please try again.', 'error');
            });
    }

    addNewEducation = () => {
        const { name, desc, passout, percentage, img } = this.state.draftEducation;
        if (!name || !desc) return showToast('Fill in every field before adding.', 'error');

        const id = this.state.education.length.toString(); // Firebase keys must be strings
        set(child(educationRef, id), { name, desc, passout: passout || '', percentage: percentage || '', img: img || '' })
            .then(() => {
                this.closeModal();
                showToast('New education added', 'success');
            })
            .catch((error) => {
                console.error('Error saving education:', error);
                showToast('Could not add this education entry - please try again.', 'error');
            });
    }

    deleteEducation = () => {
        if (this.state.editingId === null) return;
        if (!window.confirm("Delete this education entry? This can't be undone.")) return;

        // Grabbed before the Firebase record is gone - draftEducation is
        // already prefilled from this exact entry since the modal is open.
        const imagePath = storagePathFromUrl(this.state.draftEducation.img, EDUCATION_BUCKET);

        remove(child(educationRef, this.state.editingId.toString()))
            .then(async () => {
                if (imagePath) {
                    const { error } = await supabase.storage.from(EDUCATION_BUCKET).remove([imagePath]);
                    // The entry itself is already deleted either way - a
                    // leftover file in storage isn't worth blocking or
                    // re-alerting over, just log it.
                    if (error) console.error('Error deleting education logo from storage:', error);
                }
                this.closeModal();
                showToast('Education deleted', 'success');
            })
            .catch((error) => {
                console.error('Error deleting education:', error);
                showToast('Could not delete this education entry - please try again.', 'error');
            });
    }

    handleModalSubmit = () => {
        if (this.state.editingId !== null) {
            this.saveEducation();
        } else {
            this.addNewEducation();
        }
    }

    render() {
        const { education, isLoggedIn, contentLoading, draftEducation, modalOpen, modalClosing, editingId } = this.state;

        // Preserve each entry's real Firebase index as `id` before
        // reversing for display, otherwise a save after reversal would
        // write to the wrong record.
        const ordered = education
            .map((item, id) => ({ item, id }))
            .filter((entry) => entry.item)
            .slice()
            .reverse();

        return (
            <div className='base'>
                <div className='main-content-outer'>
                <h1 className="sr-only">Education</h1>
                <span className="page-watermark" aria-hidden="true">Education</span>
                {contentLoading && <PageLoader />}
                {!contentLoading && (
                <>
                <div className="education-container">
                    {ordered.map(({ item, id }) => (
                        isLoggedIn ? (
                            <div
                                className="education-card education-card-clickable"
                                key={id}
                                onClick={() => this.openEditModal(id, item)}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), this.openEditModal(id, item))}
                                role="button"
                                tabIndex={0}
                                aria-label={`Edit ${item.name}`}
                            >
                                {item.img && (
                                    <div className="education-imgBx">
                                        <img src={item.img} alt={item.name} />
                                    </div>
                                )}
                                <div className="education-contentBx">
                                    <h2>{item.name}</h2>
                                    <p>{item.desc}</p>
                                    <div className="education-meta">
                                        {item.passout && (
                                            <span className="education-meta-item">Passout: {item.passout}</span>
                                        )}
                                        {item.percentage && (
                                            <span className="education-meta-item education-meta-score">{item.percentage}</span>
                                        )}
                                    </div>
                                    <span className="education-edit-hint"><LuPencilLine /> Edit</span>
                                </div>
                            </div>
                        ) : (
                            <div className="education-card" key={id}>
                                {item.img && (
                                    <div className="education-imgBx">
                                        <img src={item.img} alt={item.name} />
                                    </div>
                                )}
                                <div className="education-contentBx">
                                    <h2>{item.name}</h2>
                                    <p>{item.desc}</p>
                                    <div className="education-meta">
                                        {item.passout && (
                                            <span className="education-meta-item">Passout: {item.passout}</span>
                                        )}
                                        {item.percentage && (
                                            <span className="education-meta-item education-meta-score">{item.percentage}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    ))}
                </div>

                {isLoggedIn && (
                    <button
                        type="button"
                        className="education-fab"
                        onClick={this.openAddModal}
                        aria-label="Add new education"
                    >
                        <LuPlus />
                    </button>
                )}

                {modalOpen && (
                    <div
                        className={`education-modal-backdrop${modalClosing ? ' is-closing' : ''}`}
                        onClick={this.closeModal}
                    >
                        <div className="education-modal-panel" onClick={(e) => e.stopPropagation()}>
                            <button
                                type="button"
                                className="education-modal-close"
                                onClick={this.closeModal}
                                aria-label="Close"
                            >
                                <LuX />
                            </button>
                            <span className="education-add-eyebrow">
                                {editingId !== null ? <><LuPencilLine /> Editing</> : <><LuSparkles /> Admin</>}
                            </span>
                            <h3 className="education-add-heading">
                                {editingId !== null ? 'Edit education' : 'Add a new education'}
                            </h3>
                            <label className="education-field">
                                <span className="education-field-label">School / institution</span>
                                <input
                                    type="text"
                                    placeholder="e.g. XYZ University"
                                    value={draftEducation.name}
                                    onChange={(e) => this.handleDraftChange('name', e.target.value)}
                                    className="education-field-input"
                                    autoFocus
                                />
                            </label>
                            <label className="education-field">
                                <span className="education-field-label">Description</span>
                                <textarea
                                    rows={3}
                                    placeholder="e.g. B.Tech in Computer Science"
                                    value={draftEducation.desc}
                                    onChange={(e) => this.handleDraftChange('desc', e.target.value)}
                                    className="education-field-input education-field-textarea"
                                />
                            </label>
                            <div className="education-field-row">
                                <label className="education-field">
                                    <span className="education-field-label"><LuCalendar /> Passout</span>
                                    <input
                                        type="text"
                                        placeholder="e.g. 2024"
                                        value={draftEducation.passout}
                                        onChange={(e) => this.handleDraftChange('passout', e.target.value)}
                                        className="education-field-input"
                                    />
                                </label>
                                <label className="education-field">
                                    <span className="education-field-label"><LuPercent /> Score</span>
                                    <input
                                        type="text"
                                        placeholder="e.g. 8.5 CGPA"
                                        value={draftEducation.percentage}
                                        onChange={(e) => this.handleDraftChange('percentage', e.target.value)}
                                        className="education-field-input"
                                    />
                                </label>
                            </div>
                            <div className="education-edit-photo">
                                <button
                                    type="button"
                                    className="education-upload-tile"
                                    onClick={this.openCropModal}
                                    aria-label="Upload school logo"
                                >
                                    {draftEducation.img ? (
                                        <img src={draftEducation.img} alt="" />
                                    ) : (
                                        <LuImagePlus className="education-upload-icon" />
                                    )}
                                </button>
                                <div className="education-upload-meta">
                                    <span className="education-upload-label">School logo</span>
                                    <span className="education-upload-hint">Optional</span>
                                    {draftEducation.img && (
                                        <button
                                            type="button"
                                            className="education-upload-remove"
                                            onClick={this.removeImage}
                                        >
                                            <LuX /> Remove photo
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className="education-modal-actions">
                                <button onClick={this.handleModalSubmit} className="app-form-button education-save-cta">
                                    {editingId !== null ? <><LuSave /> Save changes</> : <><LuPlus /> Add education</>}
                                </button>
                                {editingId !== null && (
                                    <button
                                        type="button"
                                        onClick={this.deleteEducation}
                                        className="education-delete-cta"
                                    >
                                        <LuTrash2 /> Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {this.state.cropModalOpen && (
                <div className="crop-modal-backdrop" onClick={() => this.setState({ cropModalOpen: false })}>
                <div className="crop-modal" onClick={(e) => e.stopPropagation()}>
                    <span className="crop-modal-eyebrow">Adjust logo</span>
                    <div className="cropper-wrapper">
                    <Cropper
                        image={this.state.uploadedImage}
                        crop={this.state.crop}
                        zoom={this.state.zoom}
                        aspect={1}
                        cropShape="round"
                        showGrid={true}
                        onCropChange={(crop) => this.setState({ crop })}
                        onZoomChange={(zoom) => this.setState({ zoom })}
                        onCropComplete={this.onCropComplete}
                    />
                    </div>
                    <input
                        type="range"
                        min={1}
                        max={3}
                        step={0.1}
                        value={this.state.zoom}
                        onChange={(e) => this.setState({ zoom: parseFloat(e.target.value) })}
                        className="zoom-slider"
                    />
                    <div className="crop-actions">
                    <button onClick={this.handleCropSave}>Save crop</button>
                    <button onClick={() => this.setState({ cropModalOpen: false })}>Cancel</button>
                    </div>
                </div>
                </div>
                )}

                <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    ref={(ref) => (this.fileInput = ref)}
                    onChange={this.handleFileChange}
                />
                </>
                )}
                </div>
            </div>
        )
    }
}
